import { useEffect } from 'react'

import { fetchPropertyRates } from 'actions/PropertyActions'
import { EmptyArray } from 'lib/array/arrayUtils'
import buildPropertyRatesKey from 'lib/offer/buildPropertyRatesKey'

import { useAppDispatch, useAppSelector } from './reduxHooks'

interface PropertyRatesHookParams {
  propertyId: string
  checkIn: string | undefined
  checkOut: string | undefined
  occupancies: Array<App.Occupants>
  utmContent: string | undefined
  roomTypeId?: string
  roomRateId?: string
  roomRateGroupId?: string
  disabled?: boolean
}

function usePropertyRates({
  propertyId,
  checkIn,
  checkOut,
  occupancies,
  utmContent,
  roomTypeId,
  roomRateId,
  roomRateGroupId,
  disabled = false,
}: PropertyRatesHookParams): [Array<App.RawPropertyRoomRates>, boolean, string | undefined] {
  const dispatch = useAppDispatch()

  const key = buildPropertyRatesKey({
    propertyId,
    checkIn,
    checkOut,
    occupancies,
    utmContent,
    roomTypeId,
    roomRateId,
  })
  const record = useAppSelector(state => state.offer.propertyRates[key])

  const isFetchRequired = !!key && !disabled && !record
  useEffect(() => {
    if (isFetchRequired) {
      dispatch(fetchPropertyRates(propertyId, {
        checkIn: checkIn!,
        checkOut: checkOut!,
        occupancies,
        utmContent,
        roomTypeId,
        roomRateId,
        roomRateGroupId,
      }))
    }
    // NB: `key` effectively (validates and) incorporates `propertyId`, `checkIn`, `checkOut`,
    // `occupancies`, `utmContent`, `roomTypeId`, and `roomRateId`.
    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, isFetchRequired, key])

  if (disabled) return [EmptyArray, false, undefined]
  return [
    record?.rooms ?? EmptyArray,
    record?.fetching ?? isFetchRequired,
    record?.error,
  ]
}

export default usePropertyRates
